import { MentionBoostDeliveryState, MentionBoostEligibilityStatus } from '@fluenceur/types';

/**
 * The boost footer says whose move it is. It is an action bar when the brand
 * can act, a quiet status row when it is waiting on the creator or on Meta, and
 * nothing when there is no surface to show. There is deliberately no disabled
 * button: a control you cannot press is replaced by an honest status line.
 */
export type BoostFooterTone = 'blue' | 'green' | 'orange' | 'red' | 'gray';
export type BoostFooterIconKey =
  | 'hourglass'
  | 'check'
  | 'clock'
  | 'declined'
  | 'blocked'
  | 'warning';

export type BoostFooterIntent =
  | { mode: 'skeleton' }
  | { mode: 'hidden' }
  // A destructive action (stopping a live boost) renders as a red outline
  // button; the default is the confident blue CTA (relaunch / boost).
  | { mode: 'action'; labelKey: string; onClick: () => void; destructive?: boolean }
  | {
      mode: 'status';
      tone: BoostFooterTone;
      icon: BoostFooterIconKey;
      labelKey: string;
      // A state still in motion (waiting, going live, live, stopping) breathes so
      // the row reads as alive; a settled or blocked state stays still.
      pulse?: boolean;
    };

const FOOTER = 'mentions.boost.footer';

/**
 * Delivery states a running boost can be stopped from. These are disjoint from
 * the relaunchable states (failed / stopped): a boost is either still delivering
 * (stoppable) or already settled (relaunchable), never both. Mirrors the
 * server-side stoppable set.
 */
export const STOPPABLE_DELIVERY_STATES = new Set<MentionBoostDeliveryState>([
  MentionBoostDeliveryState.PUBLISHING,
  MentionBoostDeliveryState.ACTIVE,
  MentionBoostDeliveryState.NEEDS_ATTENTION,
]);

interface EligibilityFooterInput {
  status: MentionBoostEligibilityStatus | undefined;
  canManageBoost: boolean;
  isActionable: boolean;
  isLoading: boolean;
  isError: boolean;
  onAction: () => void;
}

/**
 * Resolve the footer intent for the decision (eligibility) surface. The only
 * actionable states are the ones the brand can move on now; every other state
 * collapses to a status row whose tone and copy explain the hold.
 */
export function resolveEligibilityFooterIntent({
  status,
  canManageBoost,
  isActionable,
  isLoading,
  isError,
  onAction,
}: EligibilityFooterInput): BoostFooterIntent {
  if (isLoading) {
    return { mode: 'skeleton' };
  }

  if (isError) {
    return { mode: 'status', tone: 'gray', icon: 'warning', labelKey: `${FOOTER}.error` };
  }

  if (isActionable) {
    // A lapsed authorization (declined / revoked / expired) re-enters the flow by
    // asking the creator again, so the CTA names that ask instead of the generic
    // boost entry label.
    const needsNewAuthorization =
      status === MentionBoostEligibilityStatus.DECLINED ||
      status === MentionBoostEligibilityStatus.REVOKED ||
      status === MentionBoostEligibilityStatus.EXPIRED;

    return {
      mode: 'action',
      labelKey: needsNewAuthorization ? 'mentions.boost.confirm.requestCta' : 'mentions.boost.cta',
      onClick: onAction,
    };
  }

  switch (status) {
    case MentionBoostEligibilityStatus.PENDING:
      return {
        mode: 'status',
        tone: 'blue',
        icon: 'hourglass',
        labelKey: `${FOOTER}.pending`,
        pulse: true,
      };
    case MentionBoostEligibilityStatus.DECLINED:
      return { mode: 'status', tone: 'gray', icon: 'declined', labelKey: `${FOOTER}.declined` };
    case MentionBoostEligibilityStatus.REVOKED:
      return { mode: 'status', tone: 'gray', icon: 'declined', labelKey: `${FOOTER}.revoked` };
    case MentionBoostEligibilityStatus.EXPIRED:
      return { mode: 'status', tone: 'gray', icon: 'clock', labelKey: `${FOOTER}.expired` };
    case MentionBoostEligibilityStatus.BLOCKED:
      return { mode: 'status', tone: 'orange', icon: 'blocked', labelKey: `${FOOTER}.blocked` };
    case MentionBoostEligibilityStatus.FAILED:
      return { mode: 'status', tone: 'orange', icon: 'warning', labelKey: `${FOOTER}.failed` };
    default:
      return {
        mode: 'status',
        tone: 'gray',
        icon: 'warning',
        labelKey: canManageBoost ? `${FOOTER}.unavailable` : `${FOOTER}.adminRequired`,
      };
  }
}

/**
 * Resolve the footer intent for a launched boost. A boost still delivering
 * (publishing / active / needs attention) offers a single full-width stop
 * action; every other state is a read-only status row, and a draft shows no
 * footer. `onStop` is referenced, not invoked, so the resolver stays pure and
 * synchronously testable.
 */
export function resolveDeliveryFooterIntent(
  deliveryState: MentionBoostDeliveryState,
  onStop: () => void
): BoostFooterIntent {
  if (STOPPABLE_DELIVERY_STATES.has(deliveryState)) {
    // Stats now live inline in the pane, so the live footer is just the stop
    // control: one full-width red outline button. Both the mention modal and the
    // setup view share this shape.
    return {
      mode: 'action',
      labelKey: 'mentions.boost.stop.cta',
      onClick: onStop,
      destructive: true,
    };
  }

  switch (deliveryState) {
    case MentionBoostDeliveryState.FAILED:
      return {
        mode: 'status',
        tone: 'red',
        icon: 'warning',
        labelKey: `${FOOTER}.delivery.failed`,
      };
    case MentionBoostDeliveryState.STOPPING:
      return {
        mode: 'status',
        tone: 'gray',
        icon: 'hourglass',
        labelKey: `${FOOTER}.delivery.stopping`,
        pulse: true,
      };
    case MentionBoostDeliveryState.STOPPED:
      return {
        mode: 'status',
        tone: 'gray',
        icon: 'check',
        labelKey: `${FOOTER}.delivery.stopped`,
      };
    case MentionBoostDeliveryState.DRAFT:
    default:
      return { mode: 'hidden' };
  }
}
